import { useContext } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import { routes } from '../../assets/constants';
import { VendorInvoice, WireSettings } from '../models/billing.model';
import { downloadPdf, fetchPdfObjectUrl } from '../utils/download-file.util';

export interface HistoricalInvoiceFields {
  invoiceNumber: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  periodStart: string;
  periodEnd: string;
  issuedAt: string;
  paidAt: string;
  reference?: string;
}

/**
 * Super-admin wire-billing administration. Uses the same auth-guarded request
 * helper as the rest of the app — the stored token (a super-admin token on the
 * admin portal) drives which backend strategy authorizes the call.
 */
export const useWireAdminApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);

  const listInvoices = (status?: string): Promise<VendorInvoice[]> => {
    const url = status ? `${routes.billing.adminInvoices}?status=${encodeURIComponent(status)}` : routes.billing.adminInvoices;
    return sendAuthGuardedRequest(ApiMethod.GET, url) as Promise<VendorInvoice[]>;
  };

  const markPaid = (id: string, reference?: string): Promise<VendorInvoice> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.adminMarkPaid(id), { reference }) as Promise<VendorInvoice>;

  const voidInvoice = (id: string): Promise<VendorInvoice> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.adminVoidInvoice(id)) as Promise<VendorInvoice>;

  const createInvoice = (vendorId: string, planId: string, interval: 'month' | 'year'): Promise<VendorInvoice> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.adminCreateInvoice, { vendorId, planId, interval }) as Promise<VendorInvoice>;

  /** TEMPORARY: onboard a vendor already paying by wire (records terms; issues no invoice). */
  const importWireCustomer = (
    vendorId: string,
    body: { interval: 'month' | 'year'; amount: number; paidThrough: string; startedAt?: string },
  ): Promise<{ wirePriceAmount: number; wireInterval: 'month' | 'year'; wireCurrentPeriodEnd: string; planStartedAt: string }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.adminImportWire(vendorId), body) as Promise<{ wirePriceAmount: number; wireInterval: 'month' | 'year'; wireCurrentPeriodEnd: string; planStartedAt: string }>;

  const listVendorInvoices = (vendorId: string): Promise<VendorInvoice[]> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.billing.adminVendorInvoices(vendorId)) as Promise<VendorInvoice[]>;

  /** Record a past invoice with its original PDF; it is created paid and the vendor sees it in their history. */
  const addHistoricalInvoice = (vendorId: string, file: File, fields: HistoricalInvoiceFields): Promise<VendorInvoice> => {
    const data = new FormData();
    data.append('file', file);
    (Object.keys(fields) as (keyof HistoricalInvoiceFields)[]).forEach((key) => {
      const value = fields[key];
      if (value !== undefined && value !== '') data.append(key, String(value));
    });
    return sendAuthGuardedRequest(ApiMethod.POST, routes.billing.adminHistoricalInvoices(vendorId), data) as Promise<VendorInvoice>;
  };

  /** Delete an uploaded past invoice (the API refuses any other kind). */
  const deleteHistoricalInvoice = (invoiceId: string): Promise<{ deleted: boolean }> =>
    sendAuthGuardedRequest(ApiMethod.DELETE, routes.billing.adminInvoice(invoiceId)) as Promise<{ deleted: boolean }>;

  const getSettings = (): Promise<WireSettings> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.billing.adminWireSettings) as Promise<WireSettings>;

  const updateSettings = (patch: Partial<WireSettings>): Promise<WireSettings> =>
    sendAuthGuardedRequest(ApiMethod.PUT, routes.billing.adminWireSettings, patch) as Promise<WireSettings>;

  const runCron = (): Promise<{ expired: number; reminded: number; renewalsIssued: number; disabled: number }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.billing.adminRunWireCron) as Promise<{ expired: number; reminded: number; renewalsIssued: number; disabled: number }>;

  const downloadInvoicePdf = (invoiceId: string, invoiceNumber: string): Promise<void> =>
    downloadPdf(routes.billing.adminInvoicePdf(invoiceId), invoiceNumber);

  // Blob object URL for inline viewing (no download). Revoke when done.
  const viewInvoicePdf = (invoiceId: string): Promise<string> =>
    fetchPdfObjectUrl(routes.billing.adminInvoicePdf(invoiceId));

  return { listInvoices, markPaid, voidInvoice, createInvoice, importWireCustomer, listVendorInvoices, addHistoricalInvoice, deleteHistoricalInvoice, getSettings, updateSettings, runCron, downloadInvoicePdf, viewInvoicePdf };
};
